import { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { fetchSubscriptionPlans } from '../services/subscription';

// Module-level cache shared across every usePricingPlans() call in the app.
// PricingModal, the subscription-plan page, and the checkout page all need
// the same plan list — previously each fetched it independently on every
// mount (e.g. every time the pricing modal was opened), with duplicated
// loading/error handling. Plans change rarely, so a short TTL cache avoids
// refetching on every open/navigate while still staying reasonably fresh.
let cachedPlans = null;
let cachedAt = 0;
const CACHE_TTL_MS = 60 * 1000;

export function usePricingPlans({ enabled = true } = {}) {
	const [plans, setPlans] = useState(cachedPlans ?? []);
	const [loading, setLoading] = useState(false);
	const hasFetchedRef = useRef(Boolean(cachedPlans));

	const getPlans = useCallback(async ({ force = false } = {}) => {
		const isFresh = cachedPlans && Date.now() - cachedAt < CACHE_TTL_MS;

		if (isFresh && !force) {
			setPlans(cachedPlans);
			return cachedPlans;
		}

		try {
			setLoading(true);
			const response = await fetchSubscriptionPlans();
			const plansData = response?.plans ?? [];
			cachedPlans = plansData;
			cachedAt = Date.now();
			hasFetchedRef.current = true;
			setPlans(plansData);
			return plansData;
		} catch (error) {
			console.error(error);
			toast.error('Failed to fetch subscription plans');
			setPlans([]);
			return [];
		} finally {
			setLoading(false);
		}
	}, []);

	useEffect(() => {
		if (!enabled) return;
		getPlans();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [enabled]);

	return {
		plans,
		loading,
		refetch: () => getPlans({ force: true }),
		getPlans,
	};
}

export default usePricingPlans;
